'use client';

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
  useEffect,
} from 'react';
import Toast, { ToastProps } from './Toast';
import { setGlobalToastFunction } from '@/lib/error-handler';

interface ToastContextType {
  showToast: (toast: Omit<ToastProps, 'id' | 'onClose'>) => void;
  hideToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

interface ToastProviderProps {
  children: ReactNode;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const [toasts, setToasts] = useState<(ToastProps & { id: string })[]>([]);

  const hideToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const showToast = useCallback(
    (toast: Omit<ToastProps, 'id' | 'onClose'>) => {
      const id = crypto.randomUUID();
      const newToast: ToastProps & { id: string } = {
        ...toast,
        id,
        onClose: () => hideToast(id),
      };

      setToasts(prev => [...prev, newToast]);
    },
    [hideToast]
  );

  // Register the global toast function when the provider mounts
  useEffect(() => {
    setGlobalToastFunction(toast => {
      showToast(toast);
    });
  }, [showToast]);

  return (
    <ToastContext.Provider value={{ showToast, hideToast }}>
      {children}
      {/* Clears the header bar — at top-4 the stack sat on top of the nav. */}
      <div className="fixed right-4 top-20 z-50 max-w-md space-y-2">
        {toasts.map(toast => (
          <Toast key={toast.id} {...toast} />
        ))}
      </div>
    </ToastContext.Provider>
  );
};
